import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../store';
import api from '../../utils/api';

interface Book {
  id: number;
  title: string;
  author: string;
  category: string;
  categoryId?: number;
  format: string;
  viewCount: number;
  uploadedAt: string;
  uploader?: {
    username: string;
  };
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface PaginationInfo {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
}

interface DuplicateGroup {
  title: string;
  books: {
    id: number;
    filePath: string;
    format: string;
    author: string;
    viewCount: number;
    uploadedAt: string;
    uploader?: string;
  }[];
}

const AdminBooks: React.FC = () => {
  const history = useHistory();
  const { user } = useSelector((state: RootState) => state.auth);
  const [books, setBooks] = useState<Book[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    totalPages: 1,
    total: 0,
    pageSize: 50,
  });
  const [editedBooks, setEditedBooks] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  
  // 重复图书筛查相关状态
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<{
    autoDeletedCount: number;
    manualDuplicates: DuplicateGroup[];
    manualCount: number;
  } | null>(null);
  const [selectedBooks, setSelectedBooks] = useState<number[]>([]);
  const [deletingSelected, setDeletingSelected] = useState(false);
  
  // 重新分类相关状态
  const [reclassifying, setReclassifying] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      alert('需要管理员权限');
      history.push('/');
      return;
    }
    fetchCategories();
  }, [user, history]);

  useEffect(() => {
    fetchBooks();
  }, [pagination.page, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  const fetchBooks = async (keyword?: string) => {
    setLoading(true);
    try {
      const search = keyword !== undefined ? keyword : searchKeyword;
      const params = new URLSearchParams();
      params.append('page', String(pagination.page));
      params.append('limit', '50');
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (search) {
        params.append('search', search);
      }
      
      const { data } = await api.get(`/admin/books?${params.toString()}`);
      setBooks(data.data || data);
      if (data.total !== undefined) {
        setPagination({
          page: data.page,
          totalPages: data.totalPages,
          total: data.total,
          pageSize: data.pageSize,
        });
      }
    } catch (error) {
      console.error('获取图书列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchBooks(searchKeyword);
  };

  const handleCategoryFilterChange = (category: string) => {
    setSelectedCategory(category);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleCategoryChange = (bookId: number, categoryId: number) => {
    setEditedBooks(prev => ({
      ...prev,
      [bookId]: categoryId,
    }));
  };

  const handleSave = async () => {
    const bookIds = Object.keys(editedBooks);
    if (bookIds.length === 0) {
      alert('没有需要保存的更改');
      return;
    }

    setSaving(true);
    let successCount = 0;
    let failCount = 0;

    for (const bookId of bookIds) {
      const categoryId = editedBooks[parseInt(bookId)];
      try {
        await api.patch(`/admin/books/${bookId}`, { categoryId });
        successCount++;
      } catch (error) {
        console.error(`更新图书 ${bookId} 失败:`, error);
        failCount++;
      }
    }

    setSaving(false);
    
    if (failCount === 0) {
      alert(`成功保存 ${successCount} 条记录`);
      setEditedBooks({});
      fetchBooks();
    } else {
      alert(`保存完成：成功 ${successCount} 条，失败 ${failCount} 条`);
    }
  };

  const handleDelete = async (bookId: number) => {
    if (!window.confirm('确定要删除这本图书吗？此操作不可撤销。')) return;
    
    setDeletingId(bookId);
    try {
      await api.delete(`/admin/books/${bookId}`);
      alert('删除成功');
      fetchBooks();
    } catch (error: any) {
      console.error('删除失败:', error);
      alert(`删除失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // 重新分类所有图书
  const handleReclassify = async () => {
    if (!window.confirm('确定要根据书名重新分类所有图书吗？此操作会根据书名关键词自动匹配分类。')) {
      return;
    }
    
    setReclassifying(true);
    try {
      const { data } = await api.post('/admin/books/reclassify');
      if (data.success) {
        alert(data.message);
        // 刷新图书列表
        fetchBooks();
      } else {
        alert(data.message || '重新分类失败');
      }
    } catch (error: any) {
      console.error('重新分类失败:', error);
      alert(`重新分类失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setReclassifying(false);
    }
  };

  // 筛查重复图书
  const handleScanDuplicates = async () => {
    setScanningDuplicates(true);
    setDuplicateResult(null);
    setSelectedBooks([]);
    try {
      const { data } = await api.post('/admin/books/duplicates');
      setDuplicateResult(data);
      setShowDuplicateModal(true);
    } catch (error: any) {
      console.error('筛查失败:', error);
      alert(`筛查失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setScanningDuplicates(false);
    }
  };

  // 切换选择
  const toggleSelect = (bookId: number) => {
    setSelectedBooks(prev => 
      prev.includes(bookId)
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (!duplicateResult) return;
    
    const allIds = duplicateResult.manualDuplicates.flatMap(group => 
      group.books.map(b => b.id)
    );
    
    if (selectedBooks.length === allIds.length) {
      setSelectedBooks([]);
    } else {
      setSelectedBooks(allIds);
    }
  };

  // 批量删除选中的图书
  const handleBatchDelete = async () => {
    if (selectedBooks.length === 0) {
      alert('请选择要删除的图书');
      return;
    }

    if (!window.confirm(`确定要删除选中的 ${selectedBooks.length} 本图书吗？此操作不可撤销。`)) {
      return;
    }

    setDeletingSelected(true);
    try {
      const { data } = await api.delete('/admin/books/batch-delete', {
        data: { ids: selectedBooks },
      });
      
      if (data.success) {
        alert(data.message);
        setSelectedBooks([]);
        // 重新筛查
        handleScanDuplicates();
      } else {
        alert(data.message);
      }
    } catch (error: any) {
      console.error('批量删除失败:', error);
      alert(`删除失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setDeletingSelected(false);
    }
  };

  const getCategoryName = (categoryId: number | undefined) => {
    if (!categoryId) return '未分类';
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '未分类';
  };

  const hasChanges = Object.keys(editedBooks).length > 0;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>图书管理</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="btn btn-primary"
            onClick={handleReclassify}
            disabled={reclassifying}
            style={{ 
              opacity: reclassifying ? 0.6 : 1,
              background: '#1890ff',
              borderColor: '#1890ff',
            }}
          >
            {reclassifying ? '分类中...' : '重新分类'}
          </button>
          <button 
            className="btn btn-warning"
            onClick={handleScanDuplicates}
            disabled={scanningDuplicates}
            style={{ opacity: scanningDuplicates ? 0.6 : 1 }}
          >
            {scanningDuplicates ? '筛查中...' : '筛查重复图书'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => history.push('/admin/import')}
          >
            批量导入
          </button>
          <button 
            className="btn btn-default"
            onClick={() => history.push('/admin')}
          >
            返回后台
          </button>
        </div>
      </div>

      {/* 筛选和搜索 */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <label style={{ marginRight: '8px' }}>分类:</label>
            <select 
              value={selectedCategory}
              onChange={(e) => handleCategoryFilterChange(e.target.value)}
              className="form-input"
              style={{ width: '150px' }}
            >
              <option value="all">全部</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="搜索图书..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="form-input"
              style={{ width: '200px' }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch} className="btn btn-primary">搜索</button>
            <button 
              onClick={() => { setSearchKeyword(''); setSelectedCategory('all'); setPagination(prev => ({ ...prev, page: 1 })); fetchBooks(''); }} 
              className="btn btn-default"
            >
              重置
            </button>
          </div>
        </div>
      </div>

      {/* 操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ color: '#666' }}>
          共 {pagination.total} 本图书，第 {pagination.page}/{pagination.totalPages} 页
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {hasChanges && (
            <button 
              onClick={() => setEditedBooks({})}
              className="btn btn-default"
            >
              取消修改
            </button>
          )}
          <button 
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="btn btn-primary"
            style={{ opacity: saving ? 0.6 : 1 }}
          >
            {saving ? '保存中...' : `保存修改${hasChanges ? ` (${Object.keys(editedBooks).length}条)` : ''}`}
          </button>
        </div>
      </div>

      {/* 图书列表 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee' }}>
                <th style={{ padding: '12px', textAlign: 'left', width: '50px' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>书名</th>
                <th style={{ padding: '12px', textAlign: 'left', width: '100px' }}>作者</th>
                <th style={{ padding: '12px', textAlign: 'left', width: '150px' }}>分类</th>
                <th style={{ padding: '12px', textAlign: 'left', width: '60px' }}>格式</th>
                <th style={{ padding: '12px', textAlign: 'left', width: '60px' }}>阅读</th>
                <th style={{ padding: '12px', textAlign: 'left', width: '80px' }}>上传者</th>
                <th style={{ padding: '12px', textAlign: 'left', width: '80px' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {books.map((book) => (
                <tr key={book.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px' }}>{book.id}</td>
                  <td style={{ padding: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={book.title}>
                    {book.title}
                  </td>
                  <td style={{ padding: '12px' }}>{book.author || '未知'}</td>
                  <td style={{ padding: '12px' }}>
                    <select
                      value={editedBooks[book.id] !== undefined ? editedBooks[book.id] : (book.categoryId || '')}
                      onChange={(e) => handleCategoryChange(book.id, parseInt(e.target.value))}
                      className="form-input"
                      style={{ width: '130px', padding: '4px 8px', fontSize: '12px' }}
                    >
                      <option value="">未分类</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '12px' }}>{book.format?.toUpperCase()}</td>
                  <td style={{ padding: '12px' }}>{book.viewCount}</td>
                  <td style={{ padding: '12px' }}>{book.uploader?.username || '未知'}</td>
                  <td style={{ padding: '12px' }}>
                    <button 
                      onClick={() => handleDelete(book.id)}
                      disabled={deletingId === book.id}
                      style={{
                        padding: '4px 12px',
                        background: deletingId === book.id ? '#ccc' : '#ff4d4f',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: deletingId === book.id ? 'not-allowed' : 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {deletingId === book.id ? '删除中' : '删除'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {books.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
              暂无图书数据
            </p>
          )}
        </div>
      )}

      {/* 分页 */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '8px' }}>
          <button 
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
            className="btn btn-default"
            style={{ padding: '6px 12px', fontSize: '12px' }}
          >
            首页
          </button>
          <button 
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="btn btn-default"
            style={{ padding: '6px 12px', fontSize: '12px' }}
          >
            上一页
          </button>
          <span style={{ padding: '0 16px' }}>
            第 {pagination.page} / {pagination.totalPages} 页
          </span>
          <button 
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="btn btn-default"
            style={{ padding: '6px 12px', fontSize: '12px' }}
          >
            下一页
          </button>
          <button 
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
            className="btn btn-default"
            style={{ padding: '6px 12px', fontSize: '12px' }}
          >
            末页
          </button>
        </div>
      )}

      {/* 重复图书筛查结果模态框 */}
      {showDuplicateModal && duplicateResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}>
            <h2 style={{ marginBottom: '16px' }}>重复图书筛查结果</h2>
            
            {/* 自动处理结果 */}
            {duplicateResult.autoDeletedCount > 0 && (
              <div style={{
                padding: '12px',
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '4px',
                marginBottom: '16px',
              }}>
                已自动删除 {duplicateResult.autoDeletedCount} 本完全重复的图书（书名和文件完全一致）
              </div>
            )}

            {/* 需要人工选择的重复 */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <p style={{ color: '#666' }}>
                  书名相同但文件不同的图书共 {duplicateResult.manualCount} 本，请选择要删除的版本：
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={toggleSelectAll}
                    className="btn btn-default"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    {selectedBooks.length === duplicateResult.manualCount ? '取消全选' : '全选'}
                  </button>
                  <button 
                    onClick={handleBatchDelete}
                    disabled={selectedBooks.length === 0 || deletingSelected}
                    style={{
                      padding: '6px 12px',
                      background: deletingSelected ? '#ccc' : '#ff4d4f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: selectedBooks.length === 0 || deletingSelected ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {deletingSelected ? '删除中...' : `删除选中 (${selectedBooks.length})`}
                  </button>
                </div>
              </div>

              {duplicateResult.manualDuplicates.map((group, groupIndex) => (
                <div key={groupIndex} style={{ marginBottom: '16px', border: '1px solid #eee', borderRadius: '4px', padding: '12px' }}>
                  <h3 style={{ marginBottom: '12px', color: '#1890ff' }}>{group.title}</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ padding: '8px', textAlign: 'left', width: '40px' }}>选择</th>
                        <th style={{ padding: '8px', textAlign: 'left' }}>文件</th>
                        <th style={{ padding: '8px', textAlign: 'left', width: '60px' }}>格式</th>
                        <th style={{ padding: '8px', textAlign: 'left', width: '80px' }}>阅读量</th>
                        <th style={{ padding: '8px', textAlign: 'left', width: '80px' }}>上传者</th>
                        <th style={{ padding: '8px', textAlign: 'left', width: '80px' }}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.books.map((book) => (
                        <tr key={book.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '8px' }}>
                            <input
                              type="checkbox"
                              checked={selectedBooks.includes(book.id)}
                              onChange={() => toggleSelect(book.id)}
                            />
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={book.filePath}>
                            {book.filePath}
                          </td>
                          <td style={{ padding: '8px' }}>{book.format?.toUpperCase()}</td>
                          <td style={{ padding: '8px' }}>{book.viewCount}</td>
                          <td style={{ padding: '8px' }}>{book.uploader || '未知'}</td>
                          <td style={{ padding: '8px' }}>
                            <a 
                              href={`/read/${book.id}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#1890ff', fontSize: '12px' }}
                            >
                              阅读
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {duplicateResult.manualDuplicates.length === 0 && (
                <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  没有发现书名相同但文件不同的重复图书
                </p>
              )}
            </div>

            <div style={{ textAlign: 'right' }}>
              <button 
                onClick={() => { setShowDuplicateModal(false); fetchBooks(); }}
                className="btn btn-primary"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBooks;
